function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import useForkRef from '../private/hooks/useForkRef';
import { Close } from '../private/icons/Close';
import { ArrowBack } from '../private/icons/ArrowBack';
import { IconButton } from '../button/IconButton';
import { DrawerTitle } from './DrawerTitle';

/**
 * Indicates the primary message or task to complete within the drawer.
 * Can containt start and end addorments to help with multi-stage navigation.
 *
 * Related components: [Drawer](#drawer), [DrawerTitle](#drawertitle), [Button](#button), [IconButton](#iconbutton)
 *
 * Usage:
 *
 * ```jsx
 * import { DrawerHeader } from '@one-thd/sui-atomic-components';
 * ```
 */
const DrawerHeader = /*#__PURE__*/React.forwardRef((props, ref) => {
  const {
    children,
    endAdornment: endAdornmentProp,
    onBack,
    onClose,
    startAdornment: startAdornmentProp,
    title,
    ...other
  } = props;
  const startAdornment = onBack ? /*#__PURE__*/React.createElement(IconButton, {
    "aria-label": "back",
    onClick: onBack,
    icon: ArrowBack
  }) : startAdornmentProp;
  const endAdornment = onClose ? /*#__PURE__*/React.createElement(IconButton, {
    "aria-label": "close",
    onClick: onClose,
    icon: Close
  }) : endAdornmentProp;
  const [shadowTop, setShadowTop] = useState(0);
  const headerRef = useRef('');
  const handleRef = useForkRef(headerRef, ref);
  useEffect(() => {
    const headerHeight = headerRef.current.offsetHeight;
    setShadowTop(headerHeight);
  }, [children]);
  const hasStartAndEndAdornment = Boolean(startAdornment && endAdornment);
  const classes = classNames('drawer-header-div sui-flex-col sui-sticky sui-top-0 sui-px-2 sui-py-2 sui-bg-primary sui-z-200');
  const shadowDiv = classNames('drawer-shadow-div sui-flex-col sui-px-2 sui-pb-2 sui-bg-primary sui-sticky sui-shadow-md sui-hidden sui-z-100');
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", _extends({
    className: classes,
    ref: handleRef
  }, other), /*#__PURE__*/React.createElement("div", {
    className: "sui-flex sui-items-center sui-z-500"
  }, startAdornment != null ? /*#__PURE__*/React.createElement("div", {
    className: "sui-flex sui-grow-0 sui-shrink-0 sui-basis-auto"
  }, startAdornment) : null, /*#__PURE__*/React.createElement("div", {
    className: classNames('sui-shrink sui-basis-auto', {
      'sui-grow-0 sui-mx-auto': hasStartAndEndAdornment,
      'sui-grow': !hasStartAndEndAdornment
    })
  }, title != null ? /*#__PURE__*/React.createElement(DrawerTitle, {
    disableGutters: hasStartAndEndAdornment
  }, title) : null), endAdornment != null ? /*#__PURE__*/React.createElement("div", {
    className: "sui-flex sui-grow-0 sui-shrink-0 sui-basis-auto"
  }, endAdornment) : null), children ? /*#__PURE__*/React.createElement("div", {
    className: "sui-flex-auto sui-pt-1"
  }, children) : null), /*#__PURE__*/React.createElement("div", {
    className: shadowDiv,
    style: {
      top: shadowTop
    }
  }), /*#__PURE__*/React.createElement("div", {
    className: "drawer-shadow-div sui-absolute sui-w-full sui-pb-2 sui-bg-primary sui-hidden sui-z-100",
    style: {
      top: shadowTop + 8
    }
  }));
});
DrawerHeader.displayName = 'DrawerHeader';
DrawerHeader.propTypes = {
  /**
   * DrawerHeader children, usually DrawerTitle.
   */
  children: PropTypes.node,
  /**
   * Function to handle a close event for drawer.
   */
  onClose: PropTypes.func,
  /**
   * Function to handle the back event for nested drawer.
   */
  onBack: PropTypes.func,
  /**
   * End icons for this component.
   */
  endAdornment: PropTypes.node,
  /**
   * Start icon for this component.
   */
  startAdornment: PropTypes.node,
  /**
   * The title of the drawer header.
   */
  title: PropTypes.oneOfType([PropTypes.node, PropTypes.string])
};
export { DrawerHeader };
//# sourceMappingURL=DrawerHeader.js.map